/**
 * PasswordSecuritySection Component
 * Change password and manage two-factor authentication
 */

import { Circle, Eye, EyeOff } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { useChangePassword, useSetPassword } from "../hooks/useSettings"

/**
 * Password validation rules (matching signup validation)
 */
const validatePassword = (password: string, t: (key: string) => string): string | undefined => {
  if (password.length < 8) {
    return t("password.validationMinLength")
  }
  if (!/[A-Z]/.test(password)) {
    return t("password.validationUppercase")
  }
  if (!/[0-9]/.test(password)) {
    return t("password.validationNumber")
  }
  return undefined
}

function PasswordRequirementsTooltip({
  requirements,
  rightPos,
  belowPos,
  title,
}: {
  requirements: { label: string; met: boolean }[]
  rightPos: { top: number; left: number }
  belowPos: { top: number; left: number }
  title: string
}) {
  if (typeof document === "undefined") return null

  const content = (
    <div className="bg-[#1C1C1C] border border-dashed border-[#FFFFFF33] rounded-[20px] p-5 shadow-xl sm:w-[390px]">
      <p className="text-sm leading-6.25 font-semibold uppercase text-primary mb-2.5">{title}</p>
      <ul className="space-y-2.5">
        {requirements.map(req => (
          <li key={req.label} className="flex items-center gap-2.5">
            {req.met ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 1.6665C14.6024 1.6665 18.3334 5.39746 18.3334 9.99984C18.3334 14.6022 14.6024 18.3332 10 18.3332C5.39765 18.3332 1.66669 14.6022 1.66669 9.99984C1.66669 5.39746 5.39765 1.6665 10 1.6665ZM12.6831 7.47456L8.95835 11.1993L7.31696 9.5579C7.07288 9.31382 6.67716 9.31382 6.43308 9.5579C6.189 9.80197 6.189 10.1977 6.43308 10.4418L8.51641 12.5251C8.76049 12.7692 9.15622 12.7692 9.40029 12.5251L13.567 8.35845C13.811 8.11437 13.811 7.71864 13.567 7.47456C13.3229 7.23048 12.9272 7.23048 12.6831 7.47456Z"
                  fill="#5A5B3F"
                />
              </svg>
            ) : (
              <Circle size={18} className="text-brand-olive shrink-0" />
            )}
            <span className="text-[13px] leading-5 text-[#F2F0EBE6]">{req.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )

  return createPortal(
    <>
      <div
        className="hidden xl:block fixed z-50 transition-opacity duration-200"
        style={{ top: rightPos.top, left: rightPos.left }}
        data-password-tooltip
      >
        {content}
      </div>
      <div
        className="block xl:hidden fixed z-50 transition-opacity duration-200"
        style={{ top: belowPos.top, left: belowPos.left }}
        data-password-tooltip
      >
        {content}
      </div>
    </>,
    document.body,
  )
}

export function PasswordSecuritySection() {
  const { t } = useTranslation("settings")
  const user = useAuthStore(state => state.user)
  const hasSetPassword = user?.hasSetPassword ?? true

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [rightPos, setRightPos] = useState({ top: 0, left: 0 })
  const [belowPos, setBelowPos] = useState({ top: 0, left: 0 })

  const [backendError, setBackendError] = useState("")
  const passwordSectionRef = useRef<HTMLDivElement>(null)

  const { mutate: changePassword, isPending: isChangingPassword } = useChangePassword()
  const { mutate: setPassword, isPending: isSettingPassword } = useSetPassword()
  const logout = useAuthStore(state => state.logout)

  const isPending = isChangingPassword || isSettingPassword

  const currentPasswordError =
    currentPassword.length > 0 && currentPassword.trim() === ""
      ? t("password.validationRequired")
      : ""
  const newPasswordError = newPassword ? validatePassword(newPassword, t) : ""
  const confirmPasswordError =
    confirmPassword && newPassword !== confirmPassword ? t("password.validationMismatch") : ""

  const passwordsMatch = newPassword === confirmPassword

  const isFormValidForChangePassword =
    currentPassword.trim() &&
    newPassword.trim() &&
    confirmPassword.trim() &&
    !newPasswordError &&
    passwordsMatch

  const isFormValidForSetPassword =
    newPassword.trim() && confirmPassword.trim() && !newPasswordError && passwordsMatch

  const isFormValid = hasSetPassword ? isFormValidForChangePassword : isFormValidForSetPassword

  const requirements = useMemo(
    () => [
      {
        label: t("auth:passwordRequirements.minLength"),
        met: newPassword.length >= 8,
      },
      {
        label: t("auth:passwordRequirements.uppercaseLowercase"),
        met: /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword),
      },
      {
        label: t("auth:passwordRequirements.number"),
        met: /\d/.test(newPassword),
      },
      {
        label: t("auth:passwordRequirements.specialCharacter"),
        met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(newPassword),
      },
      {
        label: t("auth:passwordRequirements.passwordsMatch"),
        met:
          newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword,
      },
    ],
    [newPassword, confirmPassword, t],
  )

  const updateTooltipPosition = useCallback(() => {
    if (passwordSectionRef.current) {
      const sectionRect = passwordSectionRef.current.getBoundingClientRect()
      const card = passwordSectionRef.current.closest('[style*="max-width"]')
      const cardRect = card?.getBoundingClientRect() ?? sectionRect
      setRightPos({ top: sectionRect.top, left: cardRect.right + 14 })
      setBelowPos({ top: sectionRect.bottom + 8, left: sectionRect.left })
    }
  }, [])

  useEffect(() => {
    if (!passwordFocused) return

    updateTooltipPosition()

    const handleClickOutside = (e: MouseEvent) => {
      if (passwordSectionRef.current && !passwordSectionRef.current.contains(e.target as Node)) {
        const tooltipEls = document.querySelectorAll("[data-password-tooltip]")
        if ([...tooltipEls].some(el => el.contains(e.target as Node))) return
        setPasswordFocused(false)
      }
    }
    const handleFocusOutside = (e: FocusEvent) => {
      if (passwordSectionRef.current && !passwordSectionRef.current.contains(e.target as Node)) {
        setPasswordFocused(false)
      }
    }
    const handleLayoutChange = () => updateTooltipPosition()

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("focusin", handleFocusOutside)
    window.addEventListener("resize", handleLayoutChange)
    window.addEventListener("scroll", handleLayoutChange, true)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("focusin", handleFocusOutside)
      window.removeEventListener("resize", handleLayoutChange)
      window.removeEventListener("scroll", handleLayoutChange, true)
    }
  }, [passwordFocused, updateTooltipPosition])

  const handleUpdatePassword = (e: React.FormEvent) => {
    e.preventDefault()
    setBackendError("")

    if (hasSetPassword) {
      if (newPassword === currentPassword) {
        toast.error(t("password.errorSameAsCurrent"))
        return
      }

      changePassword(
        { oldPassword: currentPassword, newPassword },
        {
          onSuccess: () => {
            setCurrentPassword("")
            setNewPassword("")
            setConfirmPassword("")
            setBackendError("")
            logout()
          },
          onError: (error: unknown) => {
            const err = error as { response?: { data?: { message?: string } } }
            const message = err.response?.data?.message
            if (message?.toLowerCase().includes("current password")) {
              setBackendError(message)
            } else {
              toast.error(message || t("password.error"))
            }
          },
        },
      )
    } else {
      setPassword(newPassword, {
        onSuccess: () => {
          setNewPassword("")
          setConfirmPassword("")
          setBackendError("")
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { message?: string } } }
          toast.error(err.response?.data?.message || t("password.error"))
        },
      })
    }
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        {/* Section Header */}
        <div>
          <h1 className="text-2xl font-bold text-charcoal uppercase section-heading">
            {hasSetPassword ? t("password.title") : t("password.setPasswordTitle")}
          </h1>
          <p className="text-sm text-stone mt-1">
            {hasSetPassword ? t("password.description") : t("password.setPasswordDescription")}
          </p>
        </div>

        {/* Change/Set Password */}
        <div className="flex flex-col gap-6">
          <form onSubmit={handleUpdatePassword} className="flex flex-col gap-5 max-w-md">
            {/* Current Password - only show if user has password */}
            {hasSetPassword && (
              <div className="relative">
                <CustomInput
                  required={true}
                  id="current-password"
                  label={t("password.currentPassword")}
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={e => {
                    setCurrentPassword(e.target.value)
                    setBackendError("")
                  }}
                  error={backendError || currentPasswordError}
                  placeholder={t("password.currentPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-0 top-8 text-ash hover:text-stone cursor-pointer"
                  aria-label={
                    showCurrentPassword ? t("password.hidePassword") : t("password.showPassword")
                  }
                >
                  {showCurrentPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            )}

            {/* New Password */}
            <div ref={passwordSectionRef} className="flex flex-col gap-5">
              <div className="relative">
                <CustomInput
                  required={true}
                  id="new-password"
                  label={t("password.newPassword")}
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  error={newPasswordError}
                  placeholder={t("password.newPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-0 top-8 text-ash hover:text-stone cursor-pointer"
                  aria-label={
                    showNewPassword ? t("password.hidePassword") : t("password.showPassword")
                  }
                >
                  {showNewPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>

              {/* Confirm New Password */}
              <div className="relative">
                <CustomInput
                  required={true}
                  id="confirm-password"
                  label={t("password.confirmPassword")}
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  error={confirmPasswordError}
                  placeholder={t("password.confirmPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-0 top-8 text-ash hover:text-stone cursor-pointer"
                  aria-label={
                    showConfirmPassword ? t("password.hidePassword") : t("password.showPassword")
                  }
                >
                  {showConfirmPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            <div className="pt-5">
              <Button
                type="submit"
                variant="primary"
                size="md"
                isLoading={isPending}
                disabled={!isFormValid}
                className="px-20 uppercase"
              >
                {hasSetPassword ? t("password.save") : t("password.setPassword")}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {passwordFocused && (
        <PasswordRequirementsTooltip
          requirements={requirements}
          rightPos={rightPos}
          belowPos={belowPos}
          title={t("auth:passwordRequirements.title")}
        />
      )}
    </>
  )
}
